import { createContext, useContext, useEffect, useState, useCallback, useMemo } from "react";

/* Client-only theme: "light" | "dark".
 * - Default: auto-detect from the OS via `prefers-color-scheme`.
 * - Persisted: user choice is saved in localStorage under `client_theme`.
 * - Scope: only applies inside <CustomerLayout> (the layout reads
 *   `theme` and toggles a `data-theme` attribute on its root <div>).
 *   Admin/login/recovery screens are NOT affected — they stay dark.
 */
const ThemeContext = createContext({
  theme: "dark",
  setTheme: () => {},
  source: "system", // "system" | "user"
});

const STORAGE_KEY = "client_theme";

function getSystemPreference() {
  if (typeof window === "undefined" || !window.matchMedia) return "dark";
  return window.matchMedia("(prefers-color-scheme: light)").matches ? "light" : "dark";
}

function readInitial() {
  try {
    const v = typeof window !== "undefined" && localStorage.getItem(STORAGE_KEY);
    if (v === "light" || v === "dark") return { theme: v, source: "user" };
  } catch { /* ignore */ }
  return { theme: getSystemPreference(), source: "system" };
}

export function ClientThemeProvider({ children }) {
  const [{ theme, source }, setState] = useState(readInitial);

  // React to OS-level changes when the user is on auto-detect (no explicit choice).
  useEffect(() => {
    if (source !== "system" || typeof window === "undefined" || !window.matchMedia) return;
    const mq = window.matchMedia("(prefers-color-scheme: light)");
    const handler = (e) => setState({ theme: e.matches ? "light" : "dark", source: "system" });
    mq.addEventListener?.("change", handler);
    return () => mq.removeEventListener?.("change", handler);
  }, [source]);

  const setTheme = useCallback((next) => {
    try { localStorage.setItem(STORAGE_KEY, next); } catch { /* ignore */ }
    setState({ theme: next, source: "user" });
  }, []);

  // useMemo so the Provider value is stable when neither theme nor source
  // change — avoids needless re-renders of every consumer.
  const value = useMemo(
    () => ({ theme, setTheme, source }),
    [theme, setTheme, source]
  );

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  );
}

export function useClientTheme() {
  return useContext(ThemeContext);
}
